import { Users } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { DocumentChecklist } from '@/modulos/portal-credenciado/comum/DocumentChecklist';
import { ESTADO_DO_CADASTRO } from '@/modulos/portal-credenciado/comum/recursos';
import { ErroApi } from '@/nucleo/auth/cliente-api';
import { mascaraCpfCnpj, mascaraTelefone } from '@/nucleo/formato/documentos';
import { DetailHeader } from '@/ui/DetailHeader';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { CardSkeleton } from '@/ui/base/table-skeleton';

import { useMonitor } from './dados';
import { formatarDia, type Monitor } from './contrato';

const VOLTAR = {
  backTo: '/credenciado/monitores',
  backLabel: 'Meus Monitores',
} as const;

function Campo({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{rotulo}</dt>
      <dd className="text-sm">{valor || '—'}</dd>
    </div>
  );
}

function CursosECertificacoes({ monitor }: { monitor: Monitor }) {
  // Sem a informação na API, "Nenhum curso registrado" afirmaria o que ninguém apurou.
  const naoInformado =
    monitor.curso_primeiro_socorros === null && monitor.curso_transporte_escolar === null;
  const semCurso =
    !naoInformado && !monitor.curso_primeiro_socorros && !monitor.curso_transporte_escolar;

  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="text-base">
          <h2>Cursos e Certificações</h2>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {monitor.curso_primeiro_socorros && <Badge variant="outline">1ºs Socorros</Badge>}
          {monitor.curso_transporte_escolar && <Badge variant="outline">Transporte Escolar</Badge>}
          {semCurso && <p className="text-sm text-muted-foreground">Nenhum curso registrado</p>}
          {naoInformado && <p className="text-sm text-muted-foreground">—</p>}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * `/credenciado/monitores/:slug` — detalhe de um monitor vinculado ao
 * credenciado logado (o segmento é o id do monitor), com a composição de
 * `pages/portal/PortalMonitorDetail.tsx` do protótipo: cartão de
 * identificação com o nome como `h1`, depois Dados do Monitor, Cursos e
 * Certificações, o checklist "Documentos do Monitor" e, quando houver,
 * Observações.
 *
 * O dado vem da API (`comum/recursos.ts`): nome, CPF, estado do cadastro e
 * documentos. Os demais campos mostram "—". Tudo é consulta, e o checklist é
 * só leitura — ver `comum/DocumentChecklist.tsx`. Diferente do protótipo, o
 * "não encontrado" mantém o "voltar": sem ele a tela não tem saída.
 */
export function MonitorDetailPage() {
  const { slug: id } = useParams<{ slug: string }>();
  const { data: monitor, error, isLoading, isError, refetch } = useMonitor(id);

  if (isLoading) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <div role="status" aria-live="polite" className="space-y-6">
          <CardSkeleton />
          <CardSkeleton />
          <p className="sr-only">Carregando os dados do monitor.</p>
        </div>
      </div>
    );
  }

  if (error instanceof ErroApi && error.pedeOutroCaminho) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <ErrorState variant="forbidden" description={error.message} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar este monitor agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      </div>
    );
  }

  if (!monitor) {
    return (
      <div className="max-w-4xl space-y-6">
        <DetailHeader {...VOLTAR} />
        <EmptyState
          icon={Users}
          title="Monitor não encontrado"
          description="O registro solicitado não existe ou não está vinculado ao seu credenciamento."
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <DetailHeader {...VOLTAR} />

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold">{monitor.nome}</h1>
              <p className="mt-1 font-mono text-sm text-muted-foreground">
                {mascaraCpfCnpj(monitor.cpf)}
              </p>
              {monitor.email && (
                <p className="break-all text-sm text-muted-foreground">{monitor.email}</p>
              )}
              {monitor.telefone && (
                <p className="text-sm text-muted-foreground">{mascaraTelefone(monitor.telefone)}</p>
              )}
            </div>
            <StatusBadge
              status={ESTADO_DO_CADASTRO[monitor.situacao].selo}
              label={ESTADO_DO_CADASTRO[monitor.situacao].rotulo}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="text-base">
            <h2>Dados do Monitor</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Campo rotulo="RG" valor={monitor.rg} />
            <Campo rotulo="Data Nascimento" valor={formatarDia(monitor.data_nascimento)} />
            <Campo rotulo="Endereço" valor={monitor.endereco} />
            <Campo rotulo="Bairro" valor={monitor.bairro} />
            <Campo rotulo="CEP" valor={monitor.cep} />
            <Campo rotulo="Vínculo" valor={monitor.vinculo_empregaticio} />
          </dl>
        </CardContent>
      </Card>

      <CursosECertificacoes monitor={monitor} />

      <DocumentChecklist title="Documentos do Monitor" itens={monitor.documentos} />

      {monitor.observacoes && (
        <Card>
          <CardHeader>
            <CardTitle asChild className="text-base">
              <h2>Observações</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{monitor.observacoes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default MonitorDetailPage;
